import { useCallback } from "react";
import { Namespace, useT, type TranslationKey } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { StatusPill, useModal } from "@racoon/ui";
import { toTimestamp } from "@racoon/lib";
import {
  useArchiveContractMutation,
  useDeleteContractFilesMutation,
  useUpdateContractMutation,
  useUploadContractFilesMutation,
} from "~/shared/api/contract/contract.grpc";
import { toAmount } from "~/shared/lib/tariff-rate";
import { AddContractForm } from "../components/contracts/contract-modal.content";
import { browserFileToProtoFile } from "../lib/fileUploadUtils";

/**
 * Opens an existing contract for editing, archiving or restoring — from the
 * customer card or from anywhere a contract is shown as a link.
 */
export const useContractEditModal = () => {
  const t = useT();
  const { openModal, closeModal } = useModal();
  const [updateContract] = useUpdateContractMutation();
  const [archiveContract] = useArchiveContractMutation();
  const [uploadContractFiles] = useUploadContractFilesMutation();
  const [deleteContractFiles] = useDeleteContractFilesMutation();

  const openEdit = useCallback(
    (contract: ConnectProto.Contract, { onDone }: { onDone?: () => void } = {}) => {
      const isInactive = contract.status === ConnectProto.ContractStatus.INACTIVE;
      const owner = {
        serviceProviderId: contract.serviceProviderId,
        targetOrganizationId: contract.targetOrganizationId,
      };
      const modalId = openModal({
        closeButtonTop: "top-4",
        size: "xl",
        title: (
          <div className="flex gap-2">
            <div>{t(Namespace.Customers, "modal.editContract.title")}</div>
            <StatusPill
              tone={
                isInactive ? "bg-status-red text-brand-red" : "bg-status-green text-brand-green"
              }
            >
              {t(
                Namespace.Enum,
                `CONTRACT_STATUS_${ConnectProto.ContractStatus[contract.status]}` as TranslationKey,
              )}
            </StatusPill>
          </div>
        ),
        bodyClassName: "!p-0",
        body: (
          <AddContractForm
            editingContract={contract}
            onSubmit={async (data) => {
              const contractId = BigInt(contract.id);
              await updateContract({
                id: contractId,
                ...owner,
                dealNumber: data.dealNumber,
                name: data.name,
                startAt: data.startDate ? toTimestamp(data.startDate) : undefined,
                endAt: data.endDate ? toTimestamp(data.endDate) : undefined,
                tariffRateByJobType: data.tariffRateByJobType,
                rentCost: toAmount(data.rentCost),
                monthlyFee: toAmount(data.monthlyFee),
              }).unwrap();

              if (data.fileIdsToDelete && data.fileIdsToDelete.length > 0) {
                await deleteContractFiles({ contractId, fileIds: data.fileIdsToDelete }).unwrap();
              }
              if (data.files.length > 0) {
                const protoFiles = await Promise.all(data.files.map(browserFileToProtoFile));
                await uploadContractFiles({ contractId, files: protoFiles }).unwrap();
              }

              onDone?.();
              closeModal(modalId);
            }}
            onArchive={async () => {
              const id = BigInt(contract.id);
              if (isInactive) {
                await updateContract({
                  id,
                  ...owner,
                  status: ConnectProto.ContractStatus.ACTIVE,
                }).unwrap();
              } else {
                await archiveContract({ id, ...owner }).unwrap();
              }
              onDone?.();
              closeModal(modalId);
            }}
            onCancel={() => closeModal(modalId)}
          />
        ),
      });
    },
    [
      t,
      openModal,
      closeModal,
      updateContract,
      archiveContract,
      uploadContractFiles,
      deleteContractFiles,
    ],
  );

  return { openEdit };
};
